import { supabaseRequest, type SupabaseSession } from "../../../lib/supabase";
import { cacheMunicipalityStreets, getCachedMunicipalityStreets } from "./streetCache";
import { searchStreets } from "./streetSearch";
import type { MunicipalityStreetCache, StreetLoadResult, StreetReference } from "./types";

const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

type DatasetVersionRow = {
  id: string;
  dataset_version: string;
};

type StreetRow = {
  id: string;
  municipality_id: string;
  name: string;
  name_normalized: string;
  dataset_version_id: string;
};

export type StreetRepositoryResult = StreetLoadResult & {
  session?: SupabaseSession;
};

const isFresh = (cache: MunicipalityStreetCache) => {
  const fetchedAt = Date.parse(cache.fetchedAt);
  return Number.isFinite(fetchedAt) && Date.now() - fetchedAt < CACHE_MAX_AGE_MS;
};

export const searchCachedStreets = (
  cache: MunicipalityStreetCache | undefined,
  query: string,
  limit = 7
) => searchStreets(cache?.streets ?? [], query, limit);

export { cacheMunicipalityStreets, getCachedMunicipalityStreets };

export const getStreetsForMunicipality = async (
  municipalityId: string,
  initialSession: SupabaseSession | null
): Promise<StreetRepositoryResult> => {
  const cached = await getCachedMunicipalityStreets(municipalityId);
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;

  if (cached && (isFresh(cached) || offline || !initialSession)) {
    return { streets: cached.streets, source: "cache", offline };
  }

  if (!initialSession || offline) {
    return { streets: cached?.streets ?? [], source: cached ? "cache" : "none", offline: true };
  }

  try {
    const versionResult = await supabaseRequest<DatasetVersionRow[]>(
      "/rest/v1/market_location_dataset_versions?select=id,dataset_version&active=eq.true&order=imported_at.desc&limit=1",
      initialSession
    );
    const version = versionResult.data[0];
    if (!version) {
      return { streets: cached?.streets ?? [], source: cached ? "cache" : "none", offline: false, session: versionResult.session };
    }

    const rows: StreetRow[] = [];
    let session = versionResult.session;
    const pageSize = 1000;
    for (let offset = 0; ; offset += pageSize) {
      const streetResult = await supabaseRequest<StreetRow[]>(
        `/rest/v1/market_streets?select=id,municipality_id,name,name_normalized,dataset_version_id&municipality_id=eq.${encodeURIComponent(municipalityId)}&dataset_version_id=eq.${encodeURIComponent(version.id)}&active=eq.true&order=name.asc`,
        session,
        { headers: { Range: `${offset}-${offset + pageSize - 1}` } }
      );
      rows.push(...streetResult.data);
      session = streetResult.session;
      if (streetResult.data.length < pageSize) break;
    }

    const streets: StreetReference[] = rows.map((row) => ({
      id: row.id,
      municipalityId: row.municipality_id,
      name: row.name,
      nameNormalized: row.name_normalized,
      datasetVersion: version.dataset_version
    }));
    await cacheMunicipalityStreets({
      municipalityId,
      datasetVersion: version.dataset_version,
      fetchedAt: new Date().toISOString(),
      streets
    });

    return { streets, source: "network", offline: false, session };
  } catch {
    return {
      streets: cached?.streets ?? [],
      source: cached ? "cache" : "none",
      offline: true
    };
  }
};
